import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  FormLabel,
  InputLabel,
  Link as MuiLink,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { CheckCircleOutline, GroupsOutlined, ChecklistOutlined } from '@mui/icons-material';
import { Link } from 'react-router-dom';
import { ContentUseCase } from '../../corelogic/usecases/ContentUseCase';
import { isVisible } from '../../corelogic/model/section';
import { useLanguage } from '../../shared/contexts/LanguageContext';
import { useLocalizedPath } from '../../shared/hooks/useLocalizedPath';
import { getCardStyles, getPrimaryButtonStyles } from '../../shared/styles';
import { SEO } from './SEO';
import { BlockRenderer } from './blocks/BlockRenderer';
import { PageSections } from './PageSections';
import { Section, PageHeader } from './PageSection';

const contentUseCase = new ContentUseCase();

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const MitmachenPage: React.FC = () => {
  const theme = useTheme();
  const { language } = useLanguage();
  const lp = useLocalizedPath();
  const content = contentUseCase.getMitmachenContent(language);
  const form = content.form;
  const addedBefore = (slot: string) =>
    contentUseCase.getExtraBlocksBefore('mitmachen', slot, language);

  // The two intro cards sit in one section; either can be switched off.
  const intro = [
    { block: content.who, Icon: GroupsOutlined },
    { block: content.expect, Icon: ChecklistOutlined },
  ].filter(({ block }) => isVisible(block));

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [country, setCountry] = useState('');
  const [role, setRole] = useState('');
  const [interests, setInterests] = useState<string[]>([]);
  const [capacity, setCapacity] = useState('');
  const [message, setMessage] = useState('');
  // What the collective stands for, confirmed rather than assumed: the work
  // rests on it, so it is asked before the form can be sent - not printed
  // somewhere further down the page for a new member to find later.
  const [stanceConsent, setStanceConsent] = useState(false);
  const [privacyConsent, setPrivacyConsent] = useState(false);

  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState(false);
  const [loading, setLoading] = useState(false);
  const [touched, setTouched] = useState({
    name: false,
    email: false,
    stance: false,
    privacy: false,
  });
  // Honeypot: hidden from people, irresistible to bots. Netlify drops any
  // submission that fills it in.
  const [botField, setBotField] = useState('');

  const nameError = touched.name && !name.trim();
  const emailError = touched.email && !EMAIL_PATTERN.test(email);
  const stanceError = touched.stance && !stanceConsent;
  const privacyError = touched.privacy && !privacyConsent;

  const toggleInterest = (value: string) =>
    setInterests((current) =>
      current.includes(value) ? current.filter((item) => item !== value) : [...current, value]
    );

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setTouched({ name: true, email: true, stance: true, privacy: true });

    if (!name.trim() || !EMAIL_PATTERN.test(email) || !stanceConsent || !privacyConsent) return;

    setLoading(true);
    setSubmitError(false);

    // Netlify Forms expects a urlencoded POST to any path on the site, keyed by
    // form-name. The hidden twin of this form lives in public/index.html.
    const labelFor = (options: ReadonlyArray<{ value: string; label: string }>, value: string) =>
      options.find((option) => option.value === value)?.label ?? value;

    const payload = new URLSearchParams({
      'form-name': 'mitmachen',
      'bot-field': botField,
      name,
      email,
      phone,
      // Send the readable labels, so the spreadsheet is legible without a key.
      country: country ? labelFor(form.countryOptions, country) : '',
      role: role ? labelFor(form.roleOptions, role) : '',
      interests: interests.map((value) => labelFor(form.interestsOptions, value)).join(', '),
      capacity: capacity ? labelFor(form.capacityOptions, capacity) : '',
      message,
      language,
      stance_consent: 'true',
      privacy_consent: 'true',
    });

    try {
      const response = await fetch('/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: payload.toString(),
      });
      if (!response.ok) throw new Error('Request failed');
      setSubmitted(true);
    } catch {
      setSubmitError(true);
    } finally {
      setLoading(false);
    }
  };

  const sections: Record<string, React.ReactNode> = {
    /* Who we are looking for, and what to expect */
    intro: intro.length > 0 && (
      <Section maxWidth="lg">
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: intro.length > 1 ? 'repeat(2, 1fr)' : '1fr' },
            gap: { xs: 3, md: 4 },
          }}
        >
          {intro.map(({ block, Icon }) => (
            <Paper key={block.title} elevation={0} sx={{ ...getCardStyles(theme), '&:hover': {} }}>
              <Icon sx={{ fontSize: 32, color: 'primary.dark', mb: 2 }} />
              <Typography variant="h4" component="h2" sx={{ mb: 2.5 }}>
                {block.title}
              </Typography>
              <Box sx={{ display: 'grid', gap: 2.5 }}>
                {block.paragraphs.map((paragraph, i) => (
                  <Typography key={i} variant="body2" sx={{ color: 'text.primary' }}>
                    {paragraph}
                  </Typography>
                ))}
              </Box>
            </Paper>
          ))}
        </Box>
      </Section>
    ),

    /* Application form */
    form: isVisible(form) && (
      <Section maxWidth="sm" id="form" labelledBy="form-heading">
        {submitted ? (
          <Paper
            elevation={0}
            sx={{ ...getCardStyles(theme), textAlign: 'center', p: { xs: 4, md: 6 }, '&:hover': {} }}
            role="status"
          >
            <CheckCircleOutline sx={{ fontSize: 56, color: 'primary.dark', mb: 2 }} />
            <Typography variant="h3" component="h2" sx={{ mb: 2 }}>
              {form.successTitle}
            </Typography>
            <Typography variant="body1" sx={{ color: 'text.secondary' }}>
              {form.successMessage}
            </Typography>
          </Paper>
        ) : (
          <Paper elevation={0} sx={{ ...getCardStyles(theme), p: { xs: 3, md: 5 }, '&:hover': {} }}>
            <Typography id="form-heading" variant="h3" component="h2" sx={{ mb: 1.5 }}>
              {form.title}
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 4 }}>
              {form.description}
            </Typography>

            <Box
              component="form"
              name="mitmachen"
              method="post"
              data-netlify="true"
              onSubmit={handleSubmit}
              noValidate
              sx={{ display: 'grid', gap: 3 }}
            >
              {/* Honeypot, mirrored from the hidden form in index.html. */}
              <Box
                component="label"
                sx={{ position: 'absolute', left: -9999, width: 1, height: 1, overflow: 'hidden' }}
                aria-hidden="true"
              >
                Bitte dieses Feld leer lassen
                <input
                  name="bot-field"
                  tabIndex={-1}
                  autoComplete="off"
                  value={botField}
                  onChange={(e) => setBotField(e.target.value)}
                />
              </Box>

              <TextField
                label={`${form.nameLabel} *`}
                placeholder={form.namePlaceholder}
                value={name}
                onChange={(e) => setName(e.target.value)}
                onBlur={() => setTouched((t) => ({ ...t, name: true }))}
                error={nameError}
                helperText={nameError ? form.errors.name : ' '}
                fullWidth
              />

              <TextField
                label={`${form.emailLabel} *`}
                type="email"
                autoComplete="email"
                placeholder={form.emailPlaceholder}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onBlur={() => setTouched((t) => ({ ...t, email: true }))}
                error={emailError}
                helperText={emailError ? form.errors.email : ' '}
                fullWidth
              />

              <TextField
                label={form.phoneLabel}
                type="tel"
                autoComplete="tel"
                placeholder={form.phonePlaceholder}
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                fullWidth
              />

              <FormControl fullWidth>
                <InputLabel id="country-label">{form.countryLabel}</InputLabel>
                <Select
                  labelId="country-label"
                  value={country}
                  label={form.countryLabel}
                  onChange={(e) => setCountry(e.target.value)}
                >
                  <MenuItem value="">
                    <em>{form.countryPlaceholder}</em>
                  </MenuItem>
                  {form.countryOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth>
                <InputLabel id="role-label">{form.roleLabel}</InputLabel>
                <Select
                  labelId="role-label"
                  value={role}
                  label={form.roleLabel}
                  onChange={(e) => setRole(e.target.value)}
                >
                  <MenuItem value="">
                    <em>{form.rolePlaceholder}</em>
                  </MenuItem>
                  {form.roleOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl component="fieldset" variant="standard">
                <FormLabel component="legend" sx={{ mb: 1, color: 'text.primary', fontWeight: 600 }}>
                  {form.interestsLabel}
                </FormLabel>
                <FormHelperText sx={{ ml: 0, mb: 1 }}>{form.interestsHint}</FormHelperText>
                <FormGroup>
                  {form.interestsOptions.map((option) => (
                    <FormControlLabel
                      key={option.value}
                      control={
                        <Checkbox
                          checked={interests.includes(option.value)}
                          onChange={() => toggleInterest(option.value)}
                        />
                      }
                      label={<Typography variant="body2">{option.label}</Typography>}
                    />
                  ))}
                </FormGroup>
              </FormControl>

              <FormControl fullWidth>
                <InputLabel id="capacity-label">{form.capacityLabel}</InputLabel>
                <Select
                  labelId="capacity-label"
                  value={capacity}
                  label={form.capacityLabel}
                  onChange={(e) => setCapacity(e.target.value)}
                >
                  <MenuItem value="">
                    <em>{form.capacityPlaceholder}</em>
                  </MenuItem>
                  {form.capacityOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>{form.capacityHint}</FormHelperText>
              </FormControl>

              <TextField
                label={form.messageLabel}
                placeholder={form.messagePlaceholder}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                multiline
                minRows={4}
                fullWidth
              />

              <FormControl error={stanceError} component="fieldset" variant="standard">
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={stanceConsent}
                      onChange={(e) => setStanceConsent(e.target.checked)}
                      onBlur={() => setTouched((t) => ({ ...t, stance: true }))}
                      // The text runs to several lines: the box belongs at the
                      // first of them, not centred against the block.
                      sx={{ alignSelf: 'flex-start', mt: -0.5 }}
                    />
                  }
                  sx={{ alignItems: 'flex-start' }}
                  label={
                    <Typography variant="body2" color={stanceError ? 'error' : 'text.primary'}>
                      {form.stanceLabel} *
                    </Typography>
                  }
                />
                {stanceError && <FormHelperText sx={{ ml: 0 }}>{form.errors.stance}</FormHelperText>}
              </FormControl>

              <FormControl error={privacyError} component="fieldset" variant="standard">
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={privacyConsent}
                      onChange={(e) => setPrivacyConsent(e.target.checked)}
                      onBlur={() => setTouched((t) => ({ ...t, privacy: true }))}
                    />
                  }
                  label={
                    <Typography variant="body2" color={privacyError ? 'error' : 'text.primary'}>
                      {form.privacyLabel} *
                    </Typography>
                  }
                />
                <FormHelperText sx={{ ml: 0 }}>
                  {form.privacyNote}
                  <MuiLink component={Link} to={lp('/privacy')} sx={{ color: 'primary.dark' }}>
                    {language === 'de' ? 'Datenschutzerklärung' : 'privacy policy'}
                  </MuiLink>
                </FormHelperText>
                {privacyError && <FormHelperText>{form.errors.privacy}</FormHelperText>}
              </FormControl>

              {submitError && <Alert severity="error">{form.errorMessage}</Alert>}

              <Typography variant="caption" color="text.secondary">
                {form.requiredHint}
              </Typography>

              <Button type="submit" disabled={loading} sx={getPrimaryButtonStyles(theme)} size="large">
                {loading ? form.submittingButton : form.submitButton}
              </Button>
            </Box>
          </Paper>
        )}
      </Section>
    ),
  };

  return (
    <Box>
      <SEO page="mitmachen" />

      <BlockRenderer blocks={addedBefore('header')} />

      <PageHeader title={content.title} subtitle={content.subtitle} />

      <PageSections
        page="mitmachen"
        sections={sections}
        order={content.sectionOrder}
        addedBefore={addedBefore}
      />
    </Box>
  );
};
